import { Tool } from './base'
import {
  makeRenderer,
  PipeAndGroups,
  RenderContext,
  normalizeList,
  scalarToString,
} from './render/render-kit'
import { DataDisplayTemplate } from './render/rtd/datadisplay.types'
import { wrapRendererWithDataDisplay } from './render/rtd/datadisplay.utils'
import { NormalizedDataSource } from './render/rtd/datasource.types'
import {
  joinWithPipeBlock,
  wrapRendererWithDatasource,
} from './render/rtd/datasource.utils'
import { cortexXdrMap } from './render/rtd/tool-datasource-mappings/paloalto-cortex-xdr.datasource'
import { AstPredicate } from './render/types'

export const cortexXdrDataDisplayTemplate: DataDisplayTemplate = {
  projection: (fields) => `| fields ${fields.join(', ')}`,

  groupBy: (groupFields, aggClauses) => {
    const allClauses = [
      'min(_time) as firstSeen',
      'max(_time) as lastSeen',
      ...aggClauses,
      'count() as count',
    ]
    return `| comp ${allClauses.join(', ')} by ${groupFields.join(', ')}`
  },

  timeBucket: null,

  aggFns: {
    count: (_field, alias) => `count() as ${alias}`,
    dcount: (field, alias) => `count_distinct(${field}) as ${alias}`,
    values: (field, alias) => `values(${field}) as ${alias}`,
    collect: (field, alias) => `values(${field}) as ${alias}`,
    sum: (field, alias) => `sum(${field}) as ${alias}`,
    avg: (field, alias) => `avg(${field}) as ${alias}`,
    min: (field, alias) => `min(${field}) as ${alias}`,
    max: (field, alias) => `max(${field}) as ${alias}`,
  },

  fieldAlias: null,

  join: (body, ddClause) => `${body}\n${ddClause}`,
}

export { cortexXdrMap }

export class PaloAltoCortexXDRTool extends Tool {
  public readonly toolName = 'Palo Alto Cortex XDR'
  public readonly toolKind = 'xdr' as const

  public generateSearchPermalink(
    rootURL: string,
    renderedContent: string,
  ): string {
    const encodedRenderedContent = encodeURIComponent(renderedContent)
    //https://lexicorplab.xdr.us.paloaltonetworks.com/
    //Set time to Last 30 days
    // const currentDate = new Date();
    // const myPastDate = new Date(currentDate);
    // myPastDate.setDate(myPastDate.getDate() - 30);
    return `${rootURL}/xql/xql-search?phrase=${encodedRenderedContent}&execute_query_parameter=true`
  }

  // \ * + ? . ^ $ ( ) [ ] { } | / @ “
  public pivotEscape(key: string, value: string): string {
    return value.replace(/[\\.*+?^${}()[\]{}|/@"]/g, '\\$&')
  }

  /** Notes and Implementation
   * Grouping & Layout
   * - Uses PipeAndGroups:
   *   • Root AND → one line per child, each prefixed with `| filter `.
   *   • Nested AND/OR → inline, parenthesized as needed.
   *
   * Field mapping
   * - Uses toolTuningFields["palo_alto_cortex_xdr"]; no Eta wrapping.
   *
   * Quoting
   * - Strings are **double-quoted** with `"` escaping.
   * - **Numbers are quoted** (treated as strings) per spec.
   * - Backslashes and double quotes are escaped with `\`.
   * - Lists use `(...)` with `, ` as the delimiter.
   *
   * Operators
   * - eq:              `F = "v"`         | multi: `F in ("v1", "v2")`
   * - neq:             `F != "v"`        | multi: `F not in ("v1", "v2")`
   * - contains:        `F = "*v*"`       | multi: `F in ("*v1*", "*v2*")`
   * - doesnotcontain:  `F != "*v*"`      | multi: `F not in ("*v1*", "*v2*")`
   * - startswith:      `F = "v*"`        | multi: `F in ("v1*", "v2*")`
   * - endswith:        `F = "*v"`        | multi: `F in ("*v1", "*v2")`
   * - lt/lte/gt/gte:   `<`, `<=`, `>`, `>=`  (values **quoted**)
   * - Null/Empty:      `F = null`, `F != null`, `F = ""`, `F != ""`
   *
   * Safety
   * - Identifiers sanitized by mapField; value quoting centralized here.
   */

  public renderTuning = makeRenderer({
    tool: this.toolName,
    groupStrategy: PipeAndGroups,
    renderContext: {
      linePrefix: '| filter ',
      andGlue: ' and ',
      orGlue: ' or ',
      wrapField: (f) => f,
      // Custom quote/list: escape backslashes and double quotes.
      quote: (s: string) => `"${String(s).replace(/[\\"]/g, '\\$&')}"`,
      list: (vals: string[]) =>
        `(${vals.map((v) => `"${String(v).replace(/[\\"]/g, '\\$&')}"`).join(', ')})`,
      // Default wildcard mapping (*v*, v*, *v) from render-kit is fine.
    },
    formatPredicate: (
      p: AstPredicate,
      renderContext: RenderContext,
    ): string => {
      const F = renderContext.wrapField(renderContext.mapField(p.field))
      const vals = normalizeList(p.value).map(scalarToString)
      const isMulti = vals.length > 1

      // Apply wildcards per operator using the shared defaults
      const shaped = vals.map((s) => {
        switch (p.op) {
          case 'contains':
            return renderContext.wildcard.contains(s)
          case 'doesnotcontain':
            return renderContext.wildcard.contains(s)
          case 'startswith':
            return renderContext.wildcard.startswith(s)
          case 'endswith':
            return renderContext.wildcard.endswith(s)
          default:
            return s // eq/neq/comparisons/nulls unchanged
        }
      })

      const Q = (s: string) => renderContext.quote(s)
      const LIST = () => renderContext.list(shaped)

      switch (p.op) {
        /* null/empty */
        case 'isnull':
          return `${F} = null`
        case 'isnotnull':
          return `${F} != null`
        case 'isempty':
          return `${F} = ""`
        case 'isnotempty':
          return `${F} != ""`

        /* equality / inequality */
        case 'eq':
          return isMulti ? `${F} in ${LIST()}` : `${F} = ${Q(shaped[0])}`
        case 'neq':
          return isMulti ? `${F} not in ${LIST()}` : `${F} != ${Q(shaped[0])}`

        /* string contains/starts/ends via wildcarded equality + IN */
        case 'contains':
          return isMulti ? `${F} in ${LIST()}` : `${F} = ${Q(shaped[0])}`
        case 'doesnotcontain':
          return isMulti ? `${F} not in ${LIST()}` : `${F} != ${Q(shaped[0])}`
        case 'startswith':
          return isMulti ? `${F} in ${LIST()}` : `${F} = ${Q(shaped[0])}`
        case 'endswith':
          return isMulti ? `${F} in ${LIST()}` : `${F} = ${Q(shaped[0])}`

        /* comparisons (values quoted per spec) */
        case 'lt':
          return `${F} < ${Q(shaped[0])}`
        case 'lte':
          return `${F} <= ${Q(shaped[0])}`
        case 'gt':
          return `${F} > ${Q(shaped[0])}`
        case 'gte':
          return `${F} >= ${Q(shaped[0])}`

        default:
          // Safe default → equality behavior
          return isMulti ? `${F} in ${LIST()}` : `${F} = ${Q(shaped[0])}`
      }
    },
  })

  public readonly tuningText =
    "Insert tuning logic after the last line of base logic (ex. | filter FileName = \"*example*\"). This will typically be right before the '| fields ...' or '| comp ...' functions. NOTE: If the hunt logic's first few lines utilize '| fields ...', this may need to be removed to support all tuning fields."

  public override readonly datasourceMap = cortexXdrMap
  public readonly dataDisplayTemplate = cortexXdrDataDisplayTemplate

  /**
   * Emit the XQL dataset header + event_type / event_sub_type filter line.
   * e.g. dataset = xdr_data\n| filter event_type = ENUM.PROCESS and event_sub_type = ENUM.PROCESS_START
   */
  public emitCortexXdrDatasource(ds: NormalizedDataSource | undefined): string {
    if (!ds?.repo) return ''
    const lines: string[] = [`dataset = ${ds.repo}`]
    const filterParts: string[] = []
    if (ds.events?.length) {
      filterParts.push(
        ds.events.length === 1
          ? `event_type = ${ds.events[0]}`
          : ds.events.map((e) => `event_type = ${e}`).join(' or '),
      )
    }
    if (ds.eventCodes?.length) {
      filterParts.push(
        ds.eventCodes.length === 1
          ? `event_sub_type = ${ds.eventCodes[0]}`
          : ds.eventCodes.map((e) => `event_sub_type = ${e}`).join(' or '),
      )
    }
    if (filterParts.length) lines.push(`| filter ${filterParts.join(' and ')}`)
    return lines.join('\n')
  }

  /** Emit the full XQL prelude + tuning body + DataDisplay clause. */
  public readonly renderPaloAltoCortexXDR = wrapRendererWithDataDisplay(
    wrapRendererWithDatasource(
      this.renderTuning,
      this.toolName,
      (ds) => this.emitCortexXdrDatasource(ds),
      joinWithPipeBlock,
      this.tuningText,
    ),
    this.toolName,
    cortexXdrDataDisplayTemplate,
  )
}
